import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }) {
    const [toastState, setToastState] = useState({
        open: false,
        message: '',
        severity: 'info',
        duration: DEFAULT_DURATION,
    });

    const closeToast = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setToastState((current) => ({
            ...current,
            open: false,
        }));
    }, []);

    const showToast = useCallback((message, options = {}) => {
        if (!message) {
            return;
        }

        const nextSeverity = options.severity || 'info';
        const nextDuration = Number.isFinite(options.duration) ? options.duration : DEFAULT_DURATION;

        setToastState({
            open: true,
            message,
            severity: nextSeverity,
            duration: nextDuration,
        });
    }, []);

    const contextValue = useMemo(() => ({
        showToast,
        success: (message, options = {}) => showToast(message, { ...options, severity: 'success' }),
        error: (message, options = {}) => showToast(message, { ...options, severity: 'error' }),
        warning: (message, options = {}) => showToast(message, { ...options, severity: 'warning' }),
        info: (message, options = {}) => showToast(message, { ...options, severity: 'info' }),
        closeToast,
    }), [showToast, closeToast]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={toastState.open}
                autoHideDuration={toastState.duration}
                onClose={closeToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeToast}
                    severity={toastState.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {toastState.message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error('useToast debe usarse dentro de ToastProvider');
    }

    return context;
}
